// ─────────────────────────────────────────────────────────────────────────
// batch-39 kosten (FO-335) — uitgegeven middelen als kostenbron.
//
// De vijfde kostenbron in de medewerker-actuals: de waarde van uitgegeven
// middelen (uniek exemplaar / bulk-uitgifte / standalone dossier-item /
// kleding-uitrustingsbestelling) telt als kosten in de uitgiftemaand, met een
// harde dedupe tegen de FO-147/320-factuur-toewijzingen ("factuur wint
// volledig"). Dit bestand bevat de soort-mapping (FO-154-kostensoorten), de
// backfill-DTO's (b38/FO-329-patroon) en de gedeelde Zod-request-schema's.
//
// Conventies: bedragen als number aan de view-rand (Decimal in de service),
// datums als 'yyyy-mm-dd' (Europe/Amsterdam), enums als string-unions die de
// Prisma-enums spiegelen.
// ─────────────────────────────────────────────────────────────────────────

import { z } from 'zod'
import type { InkoopKostensoort } from './inkoop-kostensoort'
import type { VoorraadCategorie } from './voorraad'

// ─── Bron-recordtypen (spiegel van Prisma KostenBronRecordType) ──────────────

export const KOSTEN_BRON_RECORD_TYPE = [
  'UNIEK_ITEM',
  'BULK_MUTATIE',
  'UITRUSTING_ITEM',
  'UITRUSTING_BESTELLING',
  'TOEWIJZING',
] as const
export type KostenBronRecordType = (typeof KOSTEN_BRON_RECORD_TYPE)[number]

export const KOSTEN_BRON_RECORD_TYPE_LABELS: Record<KostenBronRecordType, string> = {
  UNIEK_ITEM: 'Uniek exemplaar',
  BULK_MUTATIE: 'Voorraad-uitgifte',
  UITRUSTING_ITEM: 'Uitrusting (dossier)',
  UITRUSTING_BESTELLING: 'Uitrustingsbestelling',
  TOEWIJZING: 'Kostentoewijzing',
}

// ─── Kostensoort-mapping (FO-335 §2.1, FO-154-stamtabel = enum) ───────────────
//
// Elke uitgifte krijgt bij boeking een kostensoort uit de categorie van het
// middel. De mapping is exhaustief over de enum; categorieën zonder duidelijk
// kosten-karakter vallen bewust op OVERIG (geen stille gok op iets anders).

export const VOORRAAD_CATEGORIE_KOSTENSOORT: Record<VoorraadCategorie, InkoopKostensoort> = {
  WERKKLEDING: 'KLEDING',
  PBM: 'VEILIGHEID',
  GEREEDSCHAP: 'GEREEDSCHAP',
  ELEKTRONICA: 'ELEKTRONICA',
  KOFFERS: 'GEREEDSCHAP',
  KANTOORARTIKELEN: 'OVERIG',
  VERBRUIKSMATERIAAL: 'GEREEDSCHAP',
  TELEFOON: 'ELEKTRONICA',
  SIMKAART: 'ELEKTRONICA',
  LAPTOP: 'ELEKTRONICA',
  TABLET: 'ELEKTRONICA',
  AUTOSLEUTEL: 'OVERIG',
  VOERTUIGMIDDEL: 'OVERIG',
  OVERIG: 'OVERIG',
}

/** Dossier-itemtype (Prisma `UitrustingType`) — spiegel als string-union. */
export const UITRUSTING_ITEM_TYPE = [
  'GEREEDSCHAP',
  'KLEDING',
  'TELEFOON',
  'LAPTOP',
  'PAS',
  'SLEUTEL',
  'TANKPAS',
  'LICENTIE',
  'OVERIG',
] as const
export type UitrustingItemType = (typeof UITRUSTING_ITEM_TYPE)[number]

export const UITRUSTING_TYPE_KOSTENSOORT: Record<UitrustingItemType, InkoopKostensoort> = {
  GEREEDSCHAP: 'GEREEDSCHAP',
  KLEDING: 'KLEDING',
  TELEFOON: 'ELEKTRONICA',
  LAPTOP: 'ELEKTRONICA',
  PAS: 'OVERIG',
  SLEUTEL: 'OVERIG',
  TANKPAS: 'OVERIG',
  LICENTIE: 'OVERIG',
  OVERIG: 'OVERIG',
}

/**
 * Kostensoort voor een voorraad-categorie. Onbekende (nieuwe) categorie →
 * OVERIG, zodat een enum-uitbreiding nooit een boeking blokkeert; de
 * werkvoorraad maakt zulke regels zichtbaar via de OVERIG-soort.
 */
export function kostensoortVoorVoorraadCategorie(categorie: string): InkoopKostensoort {
  return VOORRAAD_CATEGORIE_KOSTENSOORT[categorie as VoorraadCategorie] ?? 'OVERIG'
}

export function kostensoortVoorUitrustingType(type: string): InkoopKostensoort {
  return UITRUSTING_TYPE_KOSTENSOORT[type as UitrustingItemType] ?? 'OVERIG'
}

// ─── Dedupe-status (FO-335 §2.1 — "factuur wint volledig") ───────────────────

export type UitgifteDedupeStatus = 'TELT' | 'VIA_FACTUUR'

export const UITGIFTE_DEDUPE_LABELS: Record<UitgifteDedupeStatus, string> = {
  TELT: 'Telt als uitgifte',
  VIA_FACTUUR: 'Al via factuur',
}

// ─── Backfill (FO-335 §2.2, b38/FO-329-patroon) ──────────────────────────────

export const KOSTEN_BACKFILL_RUN_STATUS = ['ACTIEF', 'AFGEROND', 'GEANNULEERD'] as const
export type KostenBackfillRunStatus = (typeof KOSTEN_BACKFILL_RUN_STATUS)[number]

export const KOSTEN_BACKFILL_ITEM_STATUS = [
  'PREVIEW',
  'BEVESTIGD',
  'OVERGESLAGEN',
  'WERKVOORRAAD',
] as const
export type KostenBackfillItemStatus = (typeof KOSTEN_BACKFILL_ITEM_STATUS)[number]

export const KOSTEN_BACKFILL_ITEM_STATUS_LABELS: Record<KostenBackfillItemStatus, string> = {
  PREVIEW: 'Te beoordelen',
  BEVESTIGD: 'Geboekt',
  OVERGESLAGEN: 'Overgeslagen',
  WERKVOORRAAD: 'Geboekt zonder bedrag',
}

/**
 * Bevroren voorstel per bronrecord (previewJson). `bedrag` null = waarde
 * onbekend → bevestigen boekt als werkvoorraad-regel (zonder bedrag).
 * `previewToken` = FO-303-P3-drift-guard: bevestigen herberekent het voorstel
 * en eist een gelijk token (409 bij afwijking).
 */
export interface UitgifteBackfillPreviewDTO {
  bronType: KostenBronRecordType
  bronId: string
  employeeId: string
  employeeNaam: string
  omschrijving: string
  /** Bron-categorie (voorraad-categorie of uitrusting-type) voor weergave. */
  categorie: string
  soort: InkoopKostensoort
  soortLabel: string
  bedrag: number | null
  aantal: number
  /** Kostendatum 'yyyy-mm-dd' (Europe/Amsterdam) — de uitgiftemaand-anker. */
  datum: string
  datumGeschat: boolean
  dedupe: UitgifteDedupeStatus
  /** Bij VIA_FACTUUR: factuur-referentie voor de toelichting/doorklik. */
  dedupeFactuurId: string | null
  dedupeFactuurNr: string | null
  previewToken: string
}

/**
 * FO-339 — context-voorstel voor een bestaande toewijzing (aparte run, soort
 * CONTEXT). Puur informatief: het bedrag telt ongewijzigd bij de financiële
 * drager; het voorstel zet alleen de context-referentie (de andere kant).
 */
export interface ContextBackfillPreviewDTO {
  toewijzingId: string
  doeltype: 'MEDEWERKER' | 'VOERTUIG'
  /** De financiële drager (kenteken of medewerker-naam) voor weergave. */
  dragerNaam: string
  regelOmschrijving: string
  factuurId: string
  factuurNr: string
  datum: string
  /** Voorgestelde context (precies één gevuld, de andere kant dan de drager). */
  contextEmployeeId: string | null
  contextVoertuigId: string | null
  contextNaam: string
  /** Herleidbaarheid ("afgeleid uit toewijzing t/m ..."). */
  hint: string
  /** Informatief bedrag (telt niet mee; alleen weergave in de preview). */
  bedrag: number
  previewToken: string
}

export interface KostenBackfillItemDTO {
  id: string
  bronType: KostenBronRecordType
  bronId: string
  employeeId: string | null
  status: KostenBackfillItemStatus
  /** UITGIFTE-run: gevuld. */
  preview: UitgifteBackfillPreviewDTO | null
  /** CONTEXT-run (FO-339): gevuld. */
  contextPreview?: ContextBackfillPreviewDTO | null
  notitie: string | null
  beoordeeldOp: string | null
}

export interface KostenBackfillRunDTO {
  id: string
  soort: 'UITGIFTE' | 'CONTEXT'
  status: KostenBackfillRunStatus
  inclusiefIngenomen: boolean
  gestartDoorId: string
  gestartOp: string
  afgerondOp: string | null
  telling: {
    totaal: number
    perStatus: Record<KostenBackfillItemStatus, number>
  }
}

export interface KostenBackfillRunDetailDTO extends KostenBackfillRunDTO {
  items: KostenBackfillItemDTO[]
  rapport: KostenBackfillRapportDTO | null
}

/** Persist-vorm van het afrondings-rapport (bewijs van de inhaalslag). */
export interface KostenBackfillRapportDTO {
  afgerondOp: string
  telling: KostenBackfillRunDTO['telling']
  /** Som van de geboekte bedragen (excl. werkvoorraad-regels zonder bedrag). */
  geboektBedrag: number
  items: {
    bronType: KostenBronRecordType
    bronId: string
    employeeId: string | null
    status: KostenBackfillItemStatus
    bedrag: number | null
    datum: string | null
    notitie: string | null
  }[]
}

// ─── Werkvoorraad "waarde ontbreekt" (FO-335 §2.2/§5) ────────────────────────

export interface UitgifteWaardeOntbreektDTO {
  bronType: KostenBronRecordType
  bronId: string
  employeeId: string
  employeeNaam: string
  omschrijving: string
  soort: InkoopKostensoort
  soortLabel: string
  datum: string
  /** Waar de prijs aangevuld moet worden (artikel of exemplaar) + doorklik-id. */
  prijsBron: 'ARTIKEL' | 'EXEMPLAAR' | 'GEEN'
  prijsBronId: string | null
}

// ─── Request-schema's (gedeeld API ↔ backoffice, FO-303-conventie) ───────────

export const kostenBackfillStartSchema = z.object({
  soort: z.enum(['UITGIFTE', 'CONTEXT']),
  /** FO-335 §10 — reeds ingenomen historie meenemen (default uit). */
  inclusiefIngenomen: z.boolean().optional().default(false),
})
export type KostenBackfillStartInput = z.infer<typeof kostenBackfillStartSchema>

export const kostenBackfillBevestigSchema = z.object({
  /** Leeg/afwezig = alle open PREVIEW-items van de run (bulk-bevestiging). */
  items: z
    .array(z.object({ itemId: z.string().min(1), previewToken: z.string().min(1) }))
    .optional(),
  notitie: z.string().max(2000).optional(),
})
export type KostenBackfillBevestigInput = z.infer<typeof kostenBackfillBevestigSchema>

export const kostenBackfillOverslaanSchema = z.object({
  itemIds: z.array(z.string().min(1)).min(1),
  reden: z.string().min(1, 'Reden is verplicht').max(2000),
})
export type KostenBackfillOverslaanInput = z.infer<typeof kostenBackfillOverslaanSchema>

export const kostenBackfillAnnulerenSchema = z.object({
  reden: z.string().min(1, 'Reden is verplicht').max(2000),
})
export type KostenBackfillAnnulerenInput = z.infer<typeof kostenBackfillAnnulerenSchema>
